import React, { useCallback, useMemo } from 'react';
import { EntryPointContainer, type EntryPoint, type EntryPointComponent } from 'react-relay';
import noop from 'lodash/noop';
import { IssueViewSkeleton } from '@jira/issue__issue-view-skeleton/src/IssueViewSkeleton.tsx';
import { isWorkItemModernizationGatesEnabled } from '@jira/issue__eligibility/src/isWorkItemModernizationGatesEnabled.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point';
// eslint-disable-next-line jira/import/file-extension-required
import { PREVIEW_PANEL } from '@jira/platform__entry-points-page-layout-fields';
import { useQueryParam } from '@atlassian/react-resource-router';
import { EntryPointReferencePlaceholder } from '@jira/platform__entry-points-point-placeholder/src/index.tsx';
import type { Panel } from '@atlassian/preview-panels-api/types';
import { ISSUE_NAVIGATOR } from '@jira/platform__common-constants/src/analytics-sources.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIssueContextActions } from '@jira/issue__context-service/src/useIssueKey.tsx';
import { useListViewPreviewPanelMaxWidth } from './useListViewPreviewPanelMaxWidth.tsx';
import type { IssuePreviewPanelsNavigationRef } from './JiraPreviewPanelsContext.tsx';

export const useInitialPanel = (
	issueNavigationRef?: IssuePreviewPanelsNavigationRef,
): Panel | undefined => {
	const [selectedIssue, setSelectedIssue] = useQueryParam('selectedIssue');

	const previewPanelEntrypoint =
		usePageLayoutEntryPoint<
			EntryPoint<
				EntryPointComponent<{}, {}, {}, { fallback: () => JSX.Element | undefined }>,
				{ fallback: () => JSX.Element | undefined }
			>
		>(PREVIEW_PANEL) || null;

	const onClose = useCallback(() => {
		setSelectedIssue(undefined);
	}, [setSelectedIssue]);

	const onIssueKeyChange = useCallback(
		({ toIssueKey }: { toIssueKey: string }) => {
			toIssueKey && setSelectedIssue(toIssueKey);
		},
		[setSelectedIssue],
	);

	const [, { setDetailViewAsModal }] = useIssueContextActions();

	// Only the Issue Navigator / list-view routes opt into the wider resize bound, mirroring the
	// route gating in `PreviewPanelsProvider` so the initial panel is not widened elsewhere.
	const previewPanelMaxWidth = useListViewPreviewPanelMaxWidth();

	return useMemo<Panel | undefined>(() => {
		if (isWorkItemModernizationGatesEnabled()) {
			return undefined;
		}

		const issueNavigation =
			issueNavigationRef && expVal('nin_issue_preview_panel_navigation', 'isEnabled', false)
				? {
						onNavigateNext: () => issueNavigationRef.onNavigateNext.current?.(),
						onNavigatePrevious: () => issueNavigationRef.onNavigatePrevious.current?.(),
					}
				: undefined;

		if (selectedIssue && previewPanelEntrypoint && expVal('nin_glance_panel', 'isEnabled', false)) {
			let isModeSwitchEnabled = false;
			let onSwitchToModal = noop;
			if (expVal('nin_glance_modal', 'isEnabled', false)) {
				isModeSwitchEnabled = true;
				onSwitchToModal = () => setDetailViewAsModal();
			}

			const entryPointProps = {
				issueKey: selectedIssue,
				navigation: issueNavigation,
				onClose,
				isModeSwitchEnabled,
				analyticsSource: ISSUE_NAVIGATOR,
				onIssueKeyChange,
				onSwitchToModal,
			};
			return {
				id: `jira-issue/${selectedIssue}`,
				type: 'jira-issue',
				content: () => (
					<>
						<EntryPointReferencePlaceholder
							name="issue-preview-panel"
							fallback={<IssueViewSkeleton />}
							entryPointReference={previewPanelEntrypoint}
						>
							<EntryPointContainer
								entryPointReference={previewPanelEntrypoint}
								props={entryPointProps}
							/>
						</EntryPointReferencePlaceholder>
					</>
				),
				product: 'jira',
				// Match the wider resize bound applied client-side by IssuePreviewPanelNIN so the
				// SSR/first-paint panel is not clamped back to the default half-viewport cap when
				// re-opened at a persisted wide width.
				...(previewPanelMaxWidth && { maxWidth: previewPanelMaxWidth }),
				...(fg('jivtech-1920-back-navigation-gpp') && {
					onNavigateBack: () => {
						onIssueKeyChange({ toIssueKey: selectedIssue });
					},
				}),
			};
		}
	}, [
		issueNavigationRef,
		selectedIssue,
		previewPanelEntrypoint,
		onClose,
		onIssueKeyChange,
		setDetailViewAsModal,
		previewPanelMaxWidth,
	]);
};
